/*
 #*===================================================================
 #*
 #* Licensed Materials - Property of GameTimeFootage Inc.
 #*
 #* Copyright NextPro 2019. All Rights Reserved.
 #*
 #*===================================================================
 */

import { Injectable } from '@angular/core';

declare let ga: any;

@Injectable({
  providedIn: 'root',
})
export class GoogleAnalyticsService {
  isLoggedIn = false;
  constructor() {}

  sendPageview(newPage: string, title?: string, sport?: string) {
    if (!title && !sport && this.dontTrack(newPage)) {
      return;
    }

    const submitTitle = title || this.getPageName(newPage, this.isLoggedIn);
    const submitSport = sport || this.getSport(newPage);

    ga('set', 'page', newPage);
    ga('set', 'title', submitTitle);
    ga('set', 'dimension1', this.isLoggedIn);
    ga('set', 'dimension2', submitSport);
    ga('send', 'pageview');
  }

  sendEvent(category: string, action: string, label?: string, sport?: string) {
    ga('set', 'dimension1', this.isLoggedIn);
    ga('set', 'dimension2', this.capitalize(sport));
    ga('send', 'event', category, action, label);
  }

  private getPageName(url: string, isLoggedIn: boolean): string {
    // Login page
    if (url === '/login' || url.startsWith('/login?')) {
      return 'Login';
    }

    // Password Recovery
    if (
      url === '/login/password-recover' ||
      url === '/login/password-recover-confirm' ||
      url === '/login/verify-email'
    ) {
      return 'Password Reset';
    }

    // event details
    if (url.match(/^\/(lacrosse|soccer|football|baseball|softball|field-hockey|ice-hockey)\/(events).+/)) {
      return 'Event Details';
    }

    // Channel Details
    if (url.match(/^\/(lacrosse|soccer|football|baseball|softball|field-hockey|ice-hockey)\/(channels).+/)) {
      return 'Channel Details';
    }

    // if highlights view all
    const highlightsMatches = url.match(
      /^\/(lacrosse|soccer|football|baseball|softball|field-hockey|ice-hockey)\/(highlights)\?viewAll=(.+)/
    );
    if (highlightsMatches) {
      return this.capitalize(
        `${this.getSportName(highlightsMatches[1])} Highlights - View All ${highlightsMatches[3]}`
      );
    }

    // if sport listing page (Lacrosse Events, Baseball Channels, etc.)
    const listingMatches = url.match(
      /^\/(lacrosse|soccer|football|baseball|softball|field-hockey|ice-hockey)\/(channels|highlights|events)$/
    );
    if (listingMatches) {
      return this.capitalize(`${this.getSportName(listingMatches[1])} - ${listingMatches[2]}`);
    }

    // if highlight reel submission form
    if (url.match(/^\/my-library\/highlight-reels\/\d+$/)) {
      return 'Highlight Reel Submission';
    }

    // if profile page
    if (url.match(/^\/profile\/[^\/]+[\/\d]*$/)) {
      return 'Profile Page';
    }

    // if profile edit page
    if (url.match(/^\/profile\/[^\/]+[\/\d]*\/edit$/)) {
      return 'Profile Edit Page';
    }

    // my-account pages
    const accountMatches = url.match(/^\/my-account\/?([a-z\-]*)/);
    if (accountMatches) {
      return accountMatches[1] ? this.capitalize(accountMatches[1].replace('-', ' ')) : 'Settings';
    }

    if (url.startsWith('/my-library/highlight-reels/video/')) {
      return 'My Library - Highlight reel (video)';
    }

    if (url.startsWith('/my-library/my-clips/video/')) {
      return 'My Library - clip (video)';
    }

    if (url.startsWith('/search')) {
      return 'Search';
    }

    switch (url) {
      case '/home':
        return isLoggedIn ? 'Homepage' : 'Homepage (Unauthenticated)';

      case '/my-library/my-games':
        return 'My Library - games (grid)';

      case '/my-library/highlight-reels':
        return 'My Library - highlight reels (grid)';

      case '/my-library/my-clips':
        return 'My Library - clips (grid)';

      case '/register/confirmation':
        return 'Cart';

      case '/register/checkout':
        return 'Checkout';

      case '/register/done':
        return 'Checkout Done';

      case '/logout':
        return 'Logout';

      case '/legal/terms':
        return 'Terms & Conditions';
    }

    return url;
  }

  private getSport(url: string): string {
    const listingMatches = url.match(/^\/(lacrosse|soccer|baseball|softball|field-hockey|ice-hockey)/);
    if (listingMatches) {
      return this.capitalize(this.getSportName(listingMatches[1]));
    }

    return null;
  }

  private getSportName(sportName: string): string {
    if (!sportName) {
      return '';
    }
    return sportName.toLowerCase().replace('-', ' ');
  }

  private capitalize(text: string): string {
    if (!text) {
      return '';
    }
    return text
      .toLowerCase()
      .split(' ')
      .map((word) => `${word[0].toUpperCase()}${word.slice(1)}`)
      .join(' ');
  }

  private dontTrack(newPage: string) {
    return (
      // for video pages we want to track them only when we know the sport and video type
      newPage.startsWith('/video') ||
      newPage.startsWith('/v/') ||
      newPage.startsWith('/my-library/my-games/video') ||
      // for other pages don't track because these are only
      // used for redirecting
      newPage.startsWith('/my-profile') ||
      newPage.startsWith('/events')
    );
  }
}

export enum HIT_TYPE {
  SOCIAL = 'social',
}

export enum SOCIAL_NETWORK {
  FACEBOOK = 'Facebook',
  INSTAGRAM = 'Instagram',
  TWITTER = 'Twitter',
  LINK_COPY = 'Link Copy',
  EMBED = 'Embed Copy',
}

export enum SHARE_TYPE {
  CHANNEL = 'Channel',
  EVENT = 'Event',
  CLIP = 'Clip',
  HIGHLIGHT_REEL = 'Highlight Reel',
  HIGHLIGHT = 'Highlight',
}
